
import { cdnBaseUrl } from "@constants/baseUrlConfig";
import { useEffect, useRef, useState } from "react";
import getPathFromStorage from "../utils/getPathFromStorage";
import setPathInStorage from "../utils/setPathInStorage";

const loadingPath = "";
const errorPath = "M13 14H11V9H13M13 18H11V16H13M1 21H23L12 2L1 21Z";
const FETCHING_ICON_DATA = "...fetching_icon_data...";

interface IconUpdateEvent extends Event {
    detail?: {
        operation: string;
        iconPath: string;
        iconData: string;
    };
}

const fetchIconDataAndUpdateDataIfExistsInCache = (path: string, setIconSvgPath: (value: string) => void): void => {
    const svgPathFound = getPathFromStorage(path);
    if (!svgPathFound) {
        setPathInStorage(path, FETCHING_ICON_DATA);
        fetch(`${cdnBaseUrl}/lumenore-icons/path/${path}.txt`)
            .then((response) => response.text())
            .then((svgText) => {
                setPathInStorage(path, svgText);
                const event = new CustomEvent("icon-data-updated-event", {
                    detail: {
                        operation: "icon-data-loaded-from-api",
                        iconPath: path,
                        iconData: svgText,
                    },
                });
                window?.dispatchEvent?.(event);
            })
            .catch(() => {
                setPathInStorage(path, "");
                const event = new CustomEvent("icon-data-updated-event", {
                    detail: {
                        operation: "icon-data-loaded-from-api",
                        iconPath: path,
                        iconData: errorPath,
                    },
                });
                window?.dispatchEvent?.(event);
            });
    }
    else if (svgPathFound !== FETCHING_ICON_DATA) {
        setIconSvgPath(svgPathFound);
    }
};

function useIcon(path: string): string {
    const [iconSvgPath, setIconSvgPath] = useState<string>(loadingPath);
    const eventHandlerRef = useRef<(event: IconUpdateEvent) => void>();

    useEffect(() => {
        const handleIconDataStatusUpdate = (event: IconUpdateEvent) => {
            if (event?.detail?.operation === "icon-data-loaded-from-api" && event.detail.iconPath === path && event.detail.iconData) {
                setIconSvgPath(event.detail.iconData);
            }
            else {
                /* do nothing */
            }
        };

        if (eventHandlerRef.current) {
            window.removeEventListener("icon-data-updated-event", eventHandlerRef.current);
        }
        eventHandlerRef.current = handleIconDataStatusUpdate;
        window.addEventListener("icon-data-updated-event", handleIconDataStatusUpdate);
        fetchIconDataAndUpdateDataIfExistsInCache(path, setIconSvgPath);
        return () => {
            window.removeEventListener("icon-data-updated-event", handleIconDataStatusUpdate);
        };
    }, [path]);

    return iconSvgPath;
}

export default useIcon;
